import type { MutableRefObject } from 'react';

/** CloudFront custom-policy signing params on HLS manifest URLs. */
export type CdnSigningParams = {
    Policy: string;
    Signature: string;
    'Key-Pair-Id': string;
    expiresAt?: number;
};

export type CdnSigningQueryParams = Pick<CdnSigningParams, 'Policy' | 'Signature' | 'Key-Pair-Id'>;

/** Refresh signing credentials this many seconds before expiry. */
export const CDN_SIGNING_REFRESH_LEAD_SECONDS = 15 * 60;

export function isCloudFrontSignedUrl(url: string): boolean {
    return url.includes('Policy=') && url.includes('Signature=') && url.includes('Key-Pair-Id=');
}

export function isPrivateHlsManifestPath(url: string): boolean {
    const path = url.split('?')[0] ?? url;
    return path.includes('/CDNSource/hls/');
}

/** Default entitled HLS manifest path for an article (unsigned until CDN signing is applied). */
export function buildPrivateHlsManifestPath(articleId: number): string {
    return `/CDNSource/hls/${articleId}/${articleId}_.m3u8`;
}

/**
 * Resolve a video cdnFile for Flowplayer HLS playback.
 * Signed private HLS paths skip the legacy /HE/ → /HB/ transcode prefix rewrite.
 */
export function resolveVideoCdnFile(rawCdnFile: string, cloudfrontBaseUrl: string): string {
    if (!rawCdnFile) {
        return '';
    }

    if (isCloudFrontSignedUrl(rawCdnFile) || isPrivateHlsManifestPath(rawCdnFile)) {
        if (rawCdnFile.startsWith('https')) {
            return rawCdnFile;
        }
        return `${cloudfrontBaseUrl}${rawCdnFile}`;
    }

    let hlsURL = rawCdnFile.replace('/HE/', '/HB/');
    if (!hlsURL.startsWith('https')) {
        hlsURL = `${cloudfrontBaseUrl}${hlsURL}`;
    }
    return hlsURL;
}

export function stripUrlQueryString(url: string): string {
    return url.split('?')[0] ?? url;
}

function formatCdnSigningQueryParams(signing: CdnSigningQueryParams): string {
    return `Policy=${encodeURIComponent(signing.Policy)}&Signature=${encodeURIComponent(signing.Signature)}&Key-Pair-Id=${encodeURIComponent(
        signing['Key-Pair-Id']
    )}`;
}

export function appendCdnSigningToUrl(url: string, signing: CdnSigningQueryParams): string {
    const base = stripUrlQueryString(url);
    return `${base}?${formatCdnSigningQueryParams(signing)}`;
}

export function appendCdnSigningToRequestUrl(url: string, signing: CdnSigningQueryParams): string {
    if (isCloudFrontSignedUrl(url)) {
        return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${formatCdnSigningQueryParams(signing)}`;
}

export function getCdnSigningRefreshDelayMs(expiresAt: number | undefined, nowSeconds = Math.floor(Date.now() / 1000)): number | null {
    if (!expiresAt) {
        return null;
    }
    const refreshAt = expiresAt - CDN_SIGNING_REFRESH_LEAD_SECONDS;
    const delaySeconds = refreshAt - nowSeconds;
    if (delaySeconds <= 0) {
        return 0;
    }
    return delaySeconds * 1000;
}

export function extractCdnSigningFromUrl(url: string): CdnSigningParams | null {
    if (!isCloudFrontSignedUrl(url)) {
        return null;
    }
    const queryStart = url.indexOf('?');
    if (queryStart === -1) {
        return null;
    }
    const params = new URLSearchParams(url.slice(queryStart + 1));
    const policy = params.get('Policy');
    const signature = params.get('Signature');
    const keyPairId = params.get('Key-Pair-Id');
    if (!policy || !signature || !keyPairId) {
        return null;
    }
    return { Policy: policy, Signature: signature, 'Key-Pair-Id': keyPairId };
}

/** hls.js xhrSetup hook: append CloudFront signing query params to every HLS request. */
export function createCloudFrontHlsXhrSetup(signingRef: MutableRefObject<CdnSigningParams | null | undefined>) {
    return (xhr: XMLHttpRequest, url: string) => {
        const signing = signingRef.current;
        if (!signing) {
            return;
        }
        const signedUrl = appendCdnSigningToRequestUrl(url, signing);
        // Always call xhr.open when signing is active — hls.js skips its own open when readyState is set.
        xhr.open('GET', signedUrl, true);
    };
}
